import type { Insight } from '../domain/insight';
import { InsightType } from '../domain/insightType';
import type { TrafficChangeDirection } from '../domain/signal';
import { InsightSignalType } from '../domain/signal';

import { CountryService, WORLDWIDE_COUNTRY_ID } from './country/country';
import { fallbackLocale, SAM_I18N_PROJECT_NS, translate } from './i18n';

const metricToDisplayName: Record<string, string> = {
  total_estimated_visits: 'Monthly visits',
  total_estimated_unique: 'Unique visitors',
  total_bounce_rate: 'Bounce rate',
  total_pages_per_visit: 'Avg. pages per visit',
  total_avg_visit_duration: 'Avg. visit duration',
  total_estimated_pageviews: 'Page views',

  visits_change_yoy: 'Visits Year over Year',
  unique_change_yoy: 'Visitors Year over Year',
  unique_change_mom: 'Visitors Month over Month',
  visits_change_mom: 'Visits Month over Month',

  monthly_visits: 'Monthly visits',
  unique_monthly_visitors: 'Unique visitors',
  total_page_views: 'Page views',
  paid_search_visits: 'Paid search',
  organic_search_visits: 'Organic search',
  direct_visits: 'Direct traffic',
  social_visits: 'Social traffic',
  referrals_visits: 'Referrals traffic',
  displayads_visits: 'Display ads',
  mail_visits: 'Email traffic',
};

const directionToText: Record<TrafficChangeDirection, string> = { grew: 'growth', declined: 'decline' };

const createInsightsTextService = () => {
  const getDefaultText = (insight: Insight, ignoreCountries = false) => {
    if (insight.insightType === InsightType.SiteTrends) {
      const { metric } = insight;
      const metricText = metricToDisplayName[metric] || metric;

      return `${metricText} trend`;
    }

    if (insight.insightType === InsightType.Signal && insight.type === InsightSignalType.Traffic) {
      if ('trafficChanges' in insight.filters) {
        const { metric, country, direction } = insight.filters.trafficChanges;
        const metricText = metricToDisplayName[metric] || metric;
        const countryText =
          ignoreCountries || country === WORLDWIDE_COUNTRY_ID ? '' : ` from ${CountryService.getCountryName(country)}`;
        const directionText = directionToText[direction] || direction;

        return `${metricText}${countryText} ${directionText}`;
      }

      if ('newMarkets' in insight.filters) {
        const { countries } = insight;
        const { metric } = insight.filters.newMarkets;
        const metricText = metricToDisplayName[metric] || metric;
        let countryText = ignoreCountries ? '' : `${CountryService.getCountryName(countries[0])}`;
        if (!ignoreCountries && countries.length > 1) {
          countryText += ` and ${countries.length - 1} more`;
        }

        return `${metricText}${countryText.length > 0 ? ' from ' : ''}${countryText} threshold`;
      }
    }

    if (insight.insightType === InsightType.Opportunity) {
      const { metric } = insight.benchmark;
      const metricText = metricToDisplayName[metric] || metric;

      return `${metricText} benchmark`;
    }

    return 'Unknown insight';
  };

  const getInsightTranslatedText = (insight: Insight, lng = fallbackLocale) => {
    const getTranslatedText = ({ key, defaultText }: { key: string; defaultText: string }) => {
      return translate(key, { lng, ns: SAM_I18N_PROJECT_NS }) || defaultText;
    };

    if (insight.insightType === InsightType.SiteTrends) {
      const { metric } = insight;
      const key = `insights.email.trend.${metric}`;
      const defaultText = getDefaultText(insight, true);
      return getTranslatedText({ key, defaultText });
    }

    if (insight.insightType === InsightType.Signal && insight.type === InsightSignalType.Traffic) {
      if ('trafficChanges' in insight.filters) {
        const { metric, direction } = insight.filters.trafficChanges;
        const key = `insights.email.trafficChanges.${metric}.${direction}`;
        const defaultText = getDefaultText(insight, true);
        return getTranslatedText({ key, defaultText });
      }

      if ('newMarkets' in insight.filters) {
        const { metric } = insight.filters.newMarkets;
        const key = `insights.email.newMarkets.${metric}`;
        const defaultText = getDefaultText(insight, true);
        return getTranslatedText({ key, defaultText });
      }
    }

    if (insight.insightType === InsightType.Opportunity) {
      const { metric } = insight.benchmark;
      const key = `insights.email.benchmark.${metric}`;
      const defaultText = getDefaultText(insight, true);
      return getTranslatedText({ key, defaultText });
    }

    return 'Unknown insight';
  };

  return {
    getInsightText: (insight: Insight) => getDefaultText(insight),
    getInsightTranslatedText,
  };
};

const InsightsTextService = createInsightsTextService();

export default InsightsTextService;
